/**
 * videoPosterFrame — the ONE way this app renders a still frame for a video it
 * hosts, extracted so there is exactly one implementation instead of two.
 *
 * WHY IT EXISTS (measured live on web-stg, bundle main.378ce494.js, 2026-08-05,
 * headed Chromium, same probe reading both elements in the same session):
 *
 *   /capture      capture player   <video preload="metadata" ...>   <- correct
 *   /builder/4119 VideoWidget      <video preload="auto" poster=null ...>  <- the defect
 *
 * `preload="auto"` tells the browser to fetch the WHOLE file just so it can
 * paint the first frame. On the 1.6 KB QA fixture that is invisible; on a real
 * user's video it is the entire upload, downloaded before anyone presses play.
 *
 * The fix is the approach the capture cards already shipped, moved here so both
 * call sites share it rather than each carrying a private copy (§ shared
 * component law — a second private copy is how the two surfaces drift):
 *
 *   `#t=0.1` is a W3C media fragment. With `preload="metadata"` the browser
 *   fetches only the container header, seeks to 0.1s, and paints THAT frame as
 *   the element's still image. Frame 0 is often black on an encoder's first
 *   keyframe, which is why it is 0.1 and not 0.
 *
 *   Chosen over a `canvas.drawImage` extraction because canvas TAINTS on a
 *   cross-origin S3 object and would yield a blank thumbnail; this reads a frame
 *   with no pixel access at all, so CORS never enters into it. Chosen over a
 *   server-rendered poster because the backend has no poster field to read (the
 *   CaptureItemSerializer exposes id/type/text_content/media_key/media_url/
 *   attached_wheel/attached_at/created_at/updated_at and nothing else) — a
 *   server-side poster is a separate backend unit, not something to fake here.
 */

/** The preload mode that fetches the container header only, never the payload. */
export const VIDEO_POSTER_PRELOAD = "metadata";

/** The W3C media fragment that makes the browser paint a real frame, not black. */
export const VIDEO_POSTER_FRAGMENT = "#t=0.1";

/**
 * Extensions we host ourselves and therefore control the bytes of. A provider
 * URL (YouTube / Vimeo / Loom) is an iframe embed whose poster is the
 * provider's business — appending a media fragment to one does nothing useful
 * and risks confusing the provider's own parser, so those are returned
 * untouched. Deliberately an allow-list, not a deny-list: an unrecognised URL
 * is left ALONE, which is the safe direction.
 */
const SELF_HOSTED_VIDEO = /\.(mp4|m4v|mov|webm|ogv)(\?[^#]*)?$/i;

/**
 * Should this URL get a client-side poster frame?
 * Returns false for anything falsy, non-string, a provider embed, or a URL that
 * already carries a fragment (appending a second `#` would corrupt it).
 */
export const canRenderPosterFrame = (url) =>
  typeof url === "string" && url.length > 0 && !url.includes("#") && SELF_HOSTED_VIDEO.test(url);

/**
 * The URL to hand a <video> so it paints a real still frame. Idempotent and
 * lossless: a URL that cannot take a fragment comes back byte-identical, so a
 * caller can apply this unconditionally without branching.
 */
export const withVideoPosterFrame = (url) =>
  canRenderPosterFrame(url) ? `${url}${VIDEO_POSTER_FRAGMENT}` : url;
